import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  TextField,
  Stack,
  Alert,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { getErrorResponse, getHeader } from "../utils/util";
import axios from "axios";
import { toast } from "react-toastify";

/**
 * Props
 * ─────
 * open         : boolean – show / hide dialog
 * onClose      : () => void
 * onSubmit     : (itemsArray) => void   // fires only when user hits “Submit”
 * columnNames  : string[]               // ['name', 'email', ...]
 * title        : string (optional)
 */
const DynamicTableDialog = ({
  open,
  onClose,
  columnNames,
  url,
  validateInput,
  getPayload,
  title = "Add Items",
}) => {
  /** react-hook-form setup */
  const { control, handleSubmit, reset } = useForm({
    defaultValues: { items: [{}] }, // always start with 1 empty row
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  /** Handlers */
  const addRow = () => append({});
  const submit = async (values) => {
    console.log(values);
    const notOkMessage = validateInput(values.items);
    if (notOkMessage) {
      setError(notOkMessage);
      return;
    }
    try {
      setSaving(true);
      const payload = getPayload(values.items);
      const response = await axios.post(url, payload, getHeader());
      toast.success(response.data.message);
      onClose();
    } catch (error) {
      setError(getErrorResponse(error));
    } finally {
      setSaving(false);
      reset({ items: [{}] });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        <Table size="small">
          <TableHead>
            <TableRow>
              {columnNames.map((col) => (
                <TableCell key={col}>{col}</TableCell>
              ))}
              <TableCell width={48} /> {/* delete column */}
            </TableRow>
          </TableHead>

          <TableBody>
            {fields.map((row, rowIdx) => (
              <TableRow key={row.id}>
                {columnNames.map((col) => (
                  <TableCell key={col}>
                    <Controller
                      name={`items.${rowIdx}.${col}`}
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  </TableCell>
                ))}

                <TableCell>
                  {fields.length > 1 && (
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => remove(rowIdx)}
                    >
                      <Delete fontSize="inherit" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="outlined" startIcon={<Add />} onClick={addRow}>
            Add row
          </Button>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          loading={saving}
          variant="contained"
          onClick={handleSubmit(submit)}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DynamicTableDialog;
